import { useMemo } from "react";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Trash2, Sparkles, ThumbsUp, ThumbsDown } from "lucide-react";
import { HTMLRenderer } from "@/modern/components/ui/html-renderer";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { useFileInsights } from "@/react/hooks/entityHooks/useFileInsights";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { INSIGHT_TYPES } from "@/bryntum-lib/models/Insights";
import { Insights } from "@/bryntum-lib/models/Insights";
import { UserAvatar, BoardUser } from "@/modern/components/user-avatar";
import { User } from "@/bryntum-lib/models/User";

export interface Finding {
  id: string;
  category: string;
  description: string;
  positiveFeedbackUsers: BoardUser[];
  negativeFeedbackUsers: BoardUser[];
  insightType: string;
}

const getCategoryFromInsightType = (insightType: string): string => {
  const insightTypeConfig = Object.values(INSIGHT_TYPES).find((type) => type.value === insightType);
  return insightTypeConfig?.label || insightType;
};

export const KeyFindingsTable = () => {
  const { file, project } = useFileDetailModal();
  const { getInsights, deleteInsight } = useFileInsights();

  // Subscribe to store changes for reactive updates
  const { version: insightsVersion } = useSwivuStore({ storeId: STORE_KEYS.insightsStore });

  // Get insights for the current file
  const insights = useMemo(() => {
    if (!file?.id) return [];
    return getInsights(file.id);
  }, [file?.id, getInsights, insightsVersion]);

  // Map insights to Finding interface with all additional data
  const findings = useMemo<Finding[]>(() => {
    return insights.map((insight: Insights) => {
      // Get user records for positive feedback
      const positiveUsers: BoardUser[] = [];
      if (insight.positiveFeedbackUsers && project?.boardUsersStore) {
        insight.positiveFeedbackUsers.forEach((userId: number) => {
          const user = project.boardUsersStore.getById(userId) as User | undefined;
          if (user) {
            positiveUsers.push({
              id: user.id,
              name: user.name || "Unknown",
              profileImageURL: user.profileImageURL,
              archived: user.archived || false
            });
          }
        });
      }

      // Get user records for negative feedback
      const negativeUsers: BoardUser[] = [];
      if (insight.negativeFeedbackUsers && project?.boardUsersStore) {
        insight.negativeFeedbackUsers.forEach((userId: number) => {
          const user = project.boardUsersStore.getById(userId) as User | undefined;
          if (user) {
            negativeUsers.push({
              id: user.id,
              name: user.name || "Unknown",
              profileImageURL: user.profileImageURL,
              archived: user.archived || false
            });
          }
        });
      }

      return {
        id: String(insight.id),
        category: getCategoryFromInsightType(insight.insightType),
        description: insight.content || "",
        positiveFeedbackUsers: positiveUsers,
        negativeFeedbackUsers: negativeUsers,
        insightType: insight.insightType || ""
      };
    });
  }, [insights, project]);

  const handleRemove = (id: string) => {
    deleteInsight(id);
  };

  if (findings.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center overflow-hidden p-6">
        <div className="flex flex-col items-center gap-6 rounded-xl border border-dashed border-border/50 bg-muted/20 px-8 py-10 text-center max-w-md w-full mx-auto">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-4 ring-primary/5">
            <Sparkles className="size-7" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">No findings recorded</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">Analyze this file to generate key findings and insights.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 overflow-y-auto">
      {findings.map((finding) => (
        <div key={finding.id} className="border border-border/50 rounded-lg bg-card hover:bg-muted/20 transition-all overflow-hidden">
          <div className="p-4 space-y-4">
            {/* Header with Category and Actions */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="secondary" className="text-xs font-medium">
                  {finding.category}
                </Badge>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                onClick={() => handleRemove(finding.id)}
                aria-label={`Remove finding ${finding.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Description */}
            <HTMLRenderer content={finding.description} />

            {/* Feedback Users Section */}
            {(finding.positiveFeedbackUsers.length > 0 || finding.negativeFeedbackUsers.length > 0) && (
              <div className="pt-2 border-t border-border/50 space-y-3">
                {finding.positiveFeedbackUsers.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <ThumbsUp className="h-3.5 w-3.5 text-green-600" />
                      <span>Positive Feedback ({finding.positiveFeedbackUsers.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-5">
                      {finding.positiveFeedbackUsers.map((user) => (
                        <UserAvatar key={user.id} user={user} size="small" showTooltip={true} />
                      ))}
                    </div>
                  </div>
                )}

                {finding.negativeFeedbackUsers.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <ThumbsDown className="h-3.5 w-3.5 text-red-600" />
                      <span>Negative Feedback ({finding.negativeFeedbackUsers.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-5">
                      {finding.negativeFeedbackUsers.map((user) => (
                        <UserAvatar key={user.id} user={user} size="small" showTooltip={true} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
